import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

const S = {
  page: { background: '#0A0E1A', minHeight: '100vh', color: '#F8FAFC', fontFamily: 'system-ui, sans-serif' },
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 40px', borderBottom: '1px solid #1E3A5F44', position: 'sticky', top: 0, background: '#0A0E1Aee', backdropFilter: 'blur(10px)', zIndex: 100 },
  logo: { display: 'flex', alignItems: 'center', gap: 12 },
  logoText: { fontSize: 18, fontWeight: 900, letterSpacing: 2, color: '#F8FAFC' },
  navLinks: { display: 'flex', gap: 32, alignItems: 'center' },
  navLink: { color: '#9CA3AF', fontSize: 14, textDecoration: 'none', cursor: 'pointer' },
  btnPrimary: { background: '#1E3A5F', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  btnGold: { background: '#C9A84C', color: '#0A0E1A', border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 900 },
  btnOutline: { background: 'transparent', color: '#F8FAFC', border: '2px solid #1E3A5F', padding: '12px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  hero: { padding: '100px 40px 80px', textAlign: 'center', maxWidth: 900, margin: '0 auto' },
  heroTag: { background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#C9A84C', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, letterSpacing: 1, display: 'inline-block', marginBottom: 24 },
  heroTitle: { fontSize: 56, fontWeight: 900, lineHeight: 1.1, marginBottom: 24, letterSpacing: -1 },
  heroSub: { fontSize: 20, color: '#9CA3AF', lineHeight: 1.6, marginBottom: 40, maxWidth: 680, margin: '0 auto 40px' },
  section: { padding: '80px 40px', maxWidth: 1100, margin: '0 auto' },
  sectionTag: { fontSize: 11, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 12 },
  sectionTitle: { fontSize: 36, fontWeight: 900, marginBottom: 16, lineHeight: 1.2 },
  sectionSub: { fontSize: 16, color: '#9CA3AF', lineHeight: 1.6, maxWidth: 600, marginBottom: 48 },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 },
  featureCard: { background: '#111827', border: '1px solid #1E3A5F44', borderRadius: 12, padding: 28 },
  featureIcon: { fontSize: 32, marginBottom: 16 },
  featureTitle: { fontSize: 18, fontWeight: 800, marginBottom: 10 },
  featureDesc: { fontSize: 14, color: '#9CA3AF', lineHeight: 1.6 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24, background: '#111827', border: '1px solid #1E3A5F44', borderRadius: 16, padding: 40 },
  statItem: { textAlign: 'center' },
  statNum: { fontSize: 48, fontWeight: 900, color: '#C9A84C', lineHeight: 1 },
  statLabel: { fontSize: 13, color: '#9CA3AF', marginTop: 8 },
  plansGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 },
  planCard: (featured) => ({ background: featured ? '#1E3A5F' : '#111827', border: `2px solid ${featured ? '#C9A84C' : '#1E3A5F44'}`, borderRadius: 16, padding: 32, position: 'relative' }),
  planName: { fontSize: 22, fontWeight: 900, marginBottom: 8 },
  planPrice: { fontSize: 48, fontWeight: 900, color: '#C9A84C', lineHeight: 1, marginBottom: 4 },
  planPer: { fontSize: 14, color: '#9CA3AF', marginBottom: 24 },
  planFeature: { fontSize: 14, color: '#9CA3AF', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 },
  industriesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 },
  industryCard: { background: '#111827', border: '1px solid #1E3A5F44', borderRadius: 8, padding: 20, textAlign: 'center' },
  footer: { background: '#111827', borderTop: '1px solid #1E3A5F44', padding: '48px 40px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 },
};

const features = [
  { icon: 'ðŸ”´', title: 'Live Operations Board', desc: 'Real-time visibility of every officer, incident, and patrol across all your sites from a single command screen.' },
  { icon: 'ðŸš¨', title: 'Emergency Command', desc: 'Broadcast fire, evacuation, medical and intruder alerts instantly to all staff with one click. Track who has responded.' },
  { icon: 'ðŸ“‹', title: 'Incident Management', desc: 'Full incident lifecycle from report to approval. Version control, evidence links, police reference tracking and PDF export.' },
  { icon: 'ðŸ—ºï¸', title: 'GPS Patrol Monitoring', desc: 'Create patrol routes with GPS check-in points. Officers scan NFC tags to confirm presence. Miss a point? Control room is alerted.' },
  { icon: 'ðŸ“Š', title: 'Intelligence & Analytics', desc: 'Incident trends, officer performance metrics, site compliance scores. The data you need to make informed security decisions.' },
  { icon: 'ðŸ‘¥', title: 'Role-Based Access', desc: 'Five permission levels from Facility Manager to Officer. Everyone sees exactly what they need â€” nothing more.' },
  { icon: 'ðŸ“±', title: 'Guard App (Android)', desc: 'Purpose-built mobile app for officers. Report incidents, run patrols, complete fire tests and respond to emergencies on the go.' },
  { icon: 'ðŸ¢', title: 'Multi-Site Management', desc: 'Manage multiple buildings from one dashboard. Tenant isolation ensures each client sees only their data.' },
  { icon: 'ðŸ”’', title: 'BS 7858 Ready', desc: 'Built with SIA compliance in mind. Audit trails, version control and report approval workflows aligned to industry standards.' },
];

const industries = [
  { icon: 'ðŸ¢', name: 'Corporate HQ' },
  { icon: 'ðŸ›ï¸', name: 'Shopping Centres' },
  { icon: 'ðŸ—ï¸', name: 'Construction' },
  { icon: 'ðŸ¥', name: 'Healthcare' },
  { icon: 'ðŸŽ“', name: 'Education' },
  { icon: 'ðŸ¨', name: 'Hospitality' },
  { icon: 'ðŸ ', name: 'Residential' },
  { icon: 'ðŸ­', name: 'Industrial' },
];

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 'Â£99',
    per: '/month',
    features: ['Up to 10 officers', '1 site', 'Incident reporting', 'Basic patrol tracking', 'Email support'],
    featured: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 'Â£249',
    per: '/month',
    features: ['Up to 50 officers', '5 sites', 'Full analytics dashboard', 'NFC patrol check-in', 'Emergency alerts', 'PDF export', 'Priority support'],
    featured: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Â£599',
    per: '/month',
    features: ['Unlimited officers', 'Unlimited sites', 'White label option', 'API access', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee'],
    featured: false,
  },
];

export default function LandingPage({ onGetStarted }) {
  // Not called from the UI right now (buttons below route to "Contact sales"
  // instead) â€” kept correct and ready to re-wire once checkout fulfillment
  // is built and verified. The plan-id bug (every button passed 'starter'
  // regardless of which plan was clicked) is already fixed here.
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleStartTrial = async (plan) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      onGetStarted();
      return;
    }
    setLoadingPlan(plan);
    try {
      const functions = getFunctions(undefined, 'europe-west2');
      const createCheckout = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckout({ plan, tenantId: auth.currentUser.uid });
      window.location.href = result.data.url;
    } catch (err) {
      console.error(err);
      alert('Error starting checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleDemo = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div style={S.page}>
      {/* NAV */}
      <nav style={S.nav}>
        <div style={S.logo}>
          <img src="/osecurity-logo.png" style={{ width: 36, height: 36, borderRadius: 6 }} alt="Osecurity" />
          <div>
            <div style={S.logoText}>OSECURITY</div>
            <div style={{ fontSize: 10, color: '#9CA3AF', letterSpacing: 1 }}>OSECURE OPS</div>
          </div>
        </div>
        <div style={S.navLinks}>
          <span style={S.navLink}>Features</span>
          <span style={S.navLink}>Pricing</span>
          <span style={S.navLink}>Industries</span>
          <a href="https://synapse.osecurityltd.com" style={{ ...S.navLink, color: '#9CA3AF' }}>Login</a>
          <button style={S.btnPrimary} onClick={onGetStarted}>Get started â†’</button>
        </div>
      </nav>

      {/* HERO */}
      <div style={S.hero}>
        <div style={S.heroTag}>ðŸ”´ LIVE SECURITY INTELLIGENCE PLATFORM</div>
        <h1 style={S.heroTitle}>
          Command your security<br />
          <span style={{ color: '#C9A84C' }}>operations in real time</span>
        </h1>
        <p style={S.heroSub}>
          oSecure Ops connects your control room to every officer on the ground.
          Live incident feeds, GPS patrol tracking, emergency alerts and full compliance reporting â€”
          all in one platform built for modern security operations.
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button style={S.btnGold} onClick={onGetStarted}>Start free trial â†’</button>
          <a href="https://synapse.osecurityltd.com" style={{ textDecoration: 'none' }}>
            <button style={S.btnOutline}>View live demo</button>
          </a>
        </div>
        <div style={{ marginTop: 16, fontSize: 12, color: '#9CA3AF' }}>14-day free trial Â· No credit card required Â· Cancel anytime</div>
      </div>

      {/* STATS */}
      <div style={{ padding: '0 40px 80px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={S.statsRow}>
          <div style={S.statItem}>
            <div style={S.statNum}>99.9%</div>
            <div style={S.statLabel}>Platform uptime</div>
          </div>
          <div style={S.statItem}>
            <div style={S.statNum}>&lt;2s</div>
            <div style={S.statLabel}>Alert delivery time</div>
          </div>
          <div style={S.statItem}>
            <div style={S.statNum}>5</div>
            <div style={S.statLabel}>Permission levels</div>
          </div>
          <div style={S.statItem}>
            <div style={S.statNum}>âˆž</div>
            <div style={S.statLabel}>Sites supported</div>
          </div>
        </div>
      </div>

      {/* FEATURES */}
      <div style={S.section}>
        <div style={S.sectionTag}>Platform features</div>
        <h2 style={S.sectionTitle}>Everything your security<br />operation needs</h2>
        <p style={S.sectionSub}>From a lone officer doing rounds to a 200-person security team across 20 buildings â€” oSecure Ops scales with you.</p>
        <div style={S.featuresGrid}>
          {features.map((f, i) => (
            <div key={i} style={S.featureCard}>
              <div style={S.featureIcon}>{f.icon}</div>
              <div style={S.featureTitle}>{f.title}</div>
              <div style={S.featureDesc}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* INDUSTRIES */}
      <div style={{ ...S.section, background: '#111827', maxWidth: '100%', padding: '80px 40px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={S.sectionTag}>Built for your sector</div>
          <h2 style={S.sectionTitle}>Trusted across industries</h2>
          <p style={S.sectionSub}>Whether you're protecting a shopping centre, a hospital or a corporate headquarters â€” oSecure Ops adapts to your environment.</p>
          <div style={S.industriesGrid}>
            {industries.map((ind, i) => (
              <div key={i} style={S.industryCard}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{ind.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{ind.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div style={S.section}>
        <div style={S.sectionTag}>Pricing</div>
        <h2 style={S.sectionTitle}>Simple, transparent pricing</h2>
        <p style={S.sectionSub}>All plans include a 14-day free trial. No setup fees. No hidden costs.</p>
        <div style={S.plansGrid}>
          {plans.map((plan, i) => (
            <div key={i} style={S.planCard(plan.featured)}>
              {plan.featured && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#C9A84C', color: '#0A0E1A', padding: '4px 16px', borderRadius: 20, fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap' }}>
                  MOST POPULAR
                </div>
              )}
              <div style={S.planName}>{plan.name}</div>
              <div style={S.planPrice}>{plan.price}</div>
              <div style={S.planPer}>{plan.per} + VAT</div>
              {plan.features.map((f, j) => (
                <div key={j} style={S.planFeature}>
                  <span style={{ color: '#C9A84C' }}>âœ“</span> {f}
                </div>
              ))}
              {/* Checkout fulfillment (stripeWebhook -> subscription_status) is deployed but
                  unverified â€” never invoked, and subscription_status is read nowhere in the
                  app. Routing to Stripe checkout here would let someone pay with nothing
                  provisioned. Point at sales instead until fulfillment is built and confirmed. */}
              <a
              href={`mailto:info@osecurityltd.com?subject=${encodeURIComponent(`${plan.name} plan enquiry`)}`}
              style={{ background: '#1E3A5F', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, width: '100%', display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>
              Contact sales
            </a>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: '#111827', padding: '80px 40px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={S.sectionTag}>Get started today</div>
          <h2 style={{ ...S.sectionTitle, marginBottom: 16 }}>Ready to transform your security operations?</h2>
          <p style={{ fontSize: 16, color: '#9CA3AF', marginBottom: 32 }}>
            Join the security teams using oSecure Ops to protect their people, assets and buildings.
          </p>
          {!submitted ? (
            <form onSubmit={handleDemo} style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <input
                style={{ background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '12px 20px', borderRadius: 8, fontSize: 14, width: 280 }}
                placeholder="Enter your work email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <button type="submit" style={S.btnGold}>Request demo â†’</button>
            </form>
          ) : (
            <div style={{ background: '#2E7D3222', border: '1px solid #2E7D32', borderRadius: 12, padding: 24, color: '#2E7D32', fontWeight: 700, fontSize: 16 }}>
              âœ“ Thanks! We'll be in touch within 24 hours.
            </div>
          )}
          <div style={{ marginTop: 16, fontSize: 12, color: '#9CA3AF' }}>14-day free trial Â· No credit card required</div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={S.footer}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <img src="/osecurity-logo.png" style={{ width: 32, height: 32, borderRadius: 6 }} alt="Osecurity" />
            <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 2 }}>OSECURITY</div>
          </div>
          <div style={{ fontSize: 13, color: '#9CA3AF', maxWidth: 260, lineHeight: 1.6 }}>
            Professional. Discreet. Exceptional.<br />
            oSecurity Ltd â€” Companies House 17168865<br />
            71-75 Shelton Street, Covent Garden, London WC2H 9JQ
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Platform</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2, cursor: 'pointer' }}>oSecure Ops</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2, cursor: 'pointer' }}>Guard App</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2, cursor: 'pointer' }}>Pricing</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Company</div>
          <a href="https://osecurityltd.com" style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2, display: 'block', textDecoration: 'none' }}>osecurityltd.com</a>
          <a href="https://synapse.osecurityltd.com" style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2, display: 'block', textDecoration: 'none' }}>Control Room Login</a>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Legal</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2 }}>Privacy Policy</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2 }}>Terms of Service</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 2 }}>SIA Compliance</div>
        </div>
        <div style={{ width: '100%', borderTop: '1px solid #1E3A5F44', paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>Â© 2026 oSecurity Ltd. All rights reserved. Company No. 17168865</div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>SIA Approved Â· ISO 9001 Ready Â· GDPR Compliant</div>
        </div>
      </footer>
    </div>
  );
}